import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchMyReviews, fetchSharedReviews, fetchMySignature, fetchSentForSignature, fetchCompanySettingsAPI, updateCompanySettingsAPI, fetchEmailTemplateSettingsAPI, updateEmailTemplateSettingsAPI, fetchVeteransAffairs, acknowledgeVaUploadError, checkIsFileDeletedAPI, fetchReviewRemindersAPI, createReviewReminderAPI, updateReviewReminderAPI, deleteReviewReminderAPI, fetchAssignments, fetchMessageThreads, fetchTasks, linkAssignmentDeliverable } from "../service/home";


export const fetchMyReviewsList = createAsyncThunk(
  "myReviews/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.myReview;

      const response = await fetchMyReviews(filters);
      return response;

    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch Reviews");
    }
  }
);

export const fetchSharedReviewsList = createAsyncThunk(
  "sharedReviews/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.sharedReview;

      const response = await fetchSharedReviews(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch Shared Reviews");
    }
  }
);

export const fetchMessagesList = createAsyncThunk(
  "messages/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      return await fetchMessageThreads(getState().home.filterState.messages);
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch messages");
    }
  }
);

export const fetchMySignatureList = createAsyncThunk(
  "mySignature/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.needsMySignature;

      const response = await fetchMySignature(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch My Signature");
    }
  }
);

export const fetchSentForSignatureList = createAsyncThunk(
  "sentForSignature/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.sentForSignature;
      const response = await fetchSentForSignature(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch Sent for Signature");
    }
  }
);

export const fetchVeteransAffairsList = createAsyncThunk(
  "veteransAffairs/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.veteransAffairs;
      const response = await fetchVeteransAffairs(filters);
      return response;
    } catch (error) {
      if (error.message) {
        return rejectWithValue(error.message);
      } else if (typeof error === 'string') {
        return rejectWithValue(error);
      } else {
        return rejectWithValue("Unable to fetch Veterans Affairs data");
      }
    }
  }
);

export const acknowledgeVaUploadErrorThunk = createAsyncThunk(
  "veteransAffairs/acknowledgeError",
  async ({ id, scope, matterId }, { rejectWithValue }) => {
    try {
      const response = await acknowledgeVaUploadError(id, { scope, matterId });
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue({
        id,
        message:
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error.message ||
          "Unable to acknowledge this error.",
      });
    }
  }
);

export const fetchCompanySettings = createAsyncThunk(
  "companySettings/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const response = await fetchCompanySettingsAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch Company Settings");
    }
  }
);

export const updateCompanySettings = createAsyncThunk(
  "companySettings/update",
  async (payload, { rejectWithValue }) => {
    try {
      console.log("payload", payload);
      const response = await updateCompanySettingsAPI(payload);
      console.log("response", response);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const fetchEmailTemplateSettings = createAsyncThunk(
  "emailTemplateSettings/fetch",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const response = await fetchEmailTemplateSettingsAPI(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch Email Template Settings");
    }
  }
);

export const updateEmailTemplateSettings = createAsyncThunk(
  "emailTemplateSettings/update",
  async (payload, { rejectWithValue }) => {
    try {
      console.log("payload", payload);
      const response = await updateEmailTemplateSettingsAPI(payload);
      console.log("response", response);
      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      console.error("Error in reminding user", error);
      return rejectWithValue(error);
    }
  }
);

export const checkIsFileDeleted = createAsyncThunk(
  "documents/is-deleted",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const response = await checkIsFileDeletedAPI(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to check if file is deleted");
    }
  }
);

const reminderErrorMessage = (error, fallback) =>
  error.response?.data?.error ||
  (Array.isArray(error.response?.data?.non_field_errors)
    ? error.response.data.non_field_errors[0]
    : null) ||
  error.message ||
  fallback;

export const fetchReviewReminders = createAsyncThunk(
  "reviewReminders/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchReviewRemindersAPI();
      return response;
    } catch (error) {
      return rejectWithValue(reminderErrorMessage(error, "Unable to load reminders"));
    }
  }
);

export const createReviewReminder = createAsyncThunk(
  "reviewReminders/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createReviewReminderAPI(payload);
      return response;
    } catch (error) {
      return rejectWithValue(reminderErrorMessage(error, "Unable to create reminder"));
    }
  }
);

export const updateReviewReminder = createAsyncThunk(
  "reviewReminders/update",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateReviewReminderAPI(payload);
      return response;
    } catch (error) {
      return rejectWithValue(reminderErrorMessage(error, "Unable to update reminder"));
    }
  }
);

export const deleteReviewReminder = createAsyncThunk(
  "reviewReminders/delete",
  async (id, { rejectWithValue }) => {
    try {
      await deleteReviewReminderAPI(id);
      return id;
    } catch (error) {
      return rejectWithValue(reminderErrorMessage(error, "Unable to delete reminder"));
    }
  }
);

export const fetchAssignmentsList = createAsyncThunk(
  "assignments/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.assignments;
      return await fetchAssignments(filters);
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch assignments");
    }
  }
);

export const fetchTasksList = createAsyncThunk(
  "tasks/fetch",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = state.home.filterState.tasks;
      return await fetchTasks(filters);
    } catch (error) {
      return rejectWithValue(error.message || "Unable to fetch tasks");
    }
  }
);

/**
 * Link a draft to the assignment it answers, then refresh the list.
 *
 * The refresh is not cosmetic: linking is a precondition for the
 * partner's approval closing the assignment, so the row needs to show
 * the deliverable immediately or the associate has no confirmation the
 * chain is wired up.
 */
export const linkAssignmentDeliverableThunk = createAsyncThunk(
  "assignments/linkDeliverable",
  async ({ assignmentId, documentId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await linkAssignmentDeliverable(assignmentId, documentId);
      dispatch(fetchTasksList());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.document_id ||
          error?.response?.data?.message ||
          "Unable to link the deliverable"
      );
    }
  }
);

const HomeSlice = createSlice({
  name: "home",
  initialState: {
    myReviewsList: {},
    sharedReviewsList: {},
    messagesList: {},
    mySignatureList: {},
    sentForSignatureList: {},
    veteransAffairsList: {},
    assignmentsList: {},
    assignmentsStatus: "idle",
    assignmentsError: null,
    tasksList: {},
    tasksStatus: "idle",
    tasksError: null,
    companySettings: {},
    emailTemplate: {},
    reviewReminders: {
      rules: [],
      assignerOptions: [],
      isLoading: false,
    },
    filterState: {
      myReview: { sort_by: 'file_name', sort_direction: 'asc', page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'PENDING' },
      sharedReview: { sort_by: 'file_name', sort_direction: 'asc', page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'ALL' },
      messages: { page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'all' },
      needsMySignature: { sort_by: 'file_name', sort_direction: 'asc', page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'ALL' },
      sentForSignature: { sort_by: 'file_name', sort_direction: 'asc', page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'ALL' },
      veteransAffairs: { sort_by: 'sent_on', sort_direction: 'desc', page: 1, page_size: 25, status: '', due_on: '', search: '', chip_status: 'ALL', scope: 'mine', attorney: '' },
      // Defaults to ACTIVE, not ALL: the question this tab answers is
      // "what is outstanding", and completed work is history that would
      // sit in the way. `role` has no analogue on the review tabs --
      // it switches between work handed to you and work you handed out.
      assignments: { page: 1, page_size: 25, search: '', chip_status: 'ACTIVE', role: 'assigned_to_me' },
      tasks: { page: 1, page_size: 25, search: '', chip_status: 'ACTIVE', role: 'assigned_to_me' },
    }
  },
  reducers: {
    setFilters: (state, action) => {
      const { contextKey, filters } = action.payload;
      state.filterState[contextKey] = {
        ...state.filterState[contextKey],
        ...filters
      };
    },
    setPageSizeForAll: (state, action) => {
    const pageSize = action.payload;
    for (const key of Object.keys(state.filterState)) {
      state.filterState[key] = {
        ...state.filterState[key],
        page_size: pageSize,
        page: 1,
      };
    }
  },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyReviewsList.fulfilled, (state, action) => {
        state.myReviewsList = action.payload.data;
      })
      .addCase(fetchMyReviewsList.rejected, (state, action) => {
      })
      .addCase(fetchSharedReviewsList.fulfilled, (state, action) => {
        state.sharedReviewsList = action.payload.data;
      })
      .addCase(fetchSharedReviewsList.rejected, (state, action) => {
      })
      .addCase(fetchMessagesList.fulfilled, (state, action) => {
        state.messagesList = action.payload.data;
      })
      .addCase(fetchMessagesList.rejected, (state, action) => {
        state.messagesList = { results: [], count: 0, filter_chips: {} };
      })
      .addCase(fetchMySignatureList.fulfilled, (state, action) => {
        state.mySignatureList = action.payload.data;
      })
      .addCase(fetchMySignatureList.rejected, (state, action) => {
      })
      .addCase(fetchSentForSignatureList.fulfilled, (state, action) => {
        state.sentForSignatureList = action.payload.data;
      })
      .addCase(fetchSentForSignatureList.rejected, (state, action) => {
      })
      .addCase(fetchVeteransAffairsList.fulfilled, (state, action) => {
        state.veteransAffairsList = action.payload.data;
      })
      .addCase(fetchVeteransAffairsList.rejected, (state, action) => {
        // Set empty data on error - the error will be handled by handleError
        state.veteransAffairsList = {
          results: [],
          count: 0,
          filter_chips: {}
        };
      })
      .addCase(acknowledgeVaUploadErrorThunk.fulfilled, (state, action) => {
        // The endpoint returns fresh chip_counts computed under the same
        // scope the row was viewed under, so the Home Errors badge drops
        // immediately without waiting for a full list refetch or the
        // WebSocket broadcast (which never fires here — status is
        // unchanged).
        const { id, error_acknowledged_at, chip_counts } = action.payload;
        const row = state.veteransAffairsList?.results?.find((r) => r.id === id);
        if (row) {
          row.error_acknowledged_at = error_acknowledged_at;
        }
        if (state.veteransAffairsList && chip_counts) {
          state.veteransAffairsList.chip_counts = chip_counts;
        }
      })
      .addCase(fetchAssignmentsList.pending, (state) => {
        state.assignmentsStatus = "loading";
      })
      .addCase(fetchAssignmentsList.fulfilled, (state, action) => {
        state.assignmentsStatus = "succeeded";
        state.assignmentsError = null;
        state.assignmentsList = action.payload.data;
      })
      .addCase(fetchAssignmentsList.rejected, (state, action) => {
        state.assignmentsStatus = "failed";
        state.assignmentsError = action.payload;
        // An empty shape rather than the last good one: showing stale
        // assignments after a failed refresh is worse than showing none,
        // because the user cannot tell the list is out of date.
        state.assignmentsList = { results: [], count: 0, chip_counts: {} };
      })
      .addCase(fetchTasksList.pending, (state) => {
        state.tasksStatus = "loading";
      })
      .addCase(fetchTasksList.fulfilled, (state, action) => {
        state.tasksStatus = "succeeded";
        state.tasksError = null;
        state.tasksList = action.payload.data;
      })
      .addCase(fetchTasksList.rejected, (state, action) => {
        state.tasksStatus = "failed";
        state.tasksError = action.payload;
        state.tasksList = { results: [], count: 0, chip_counts: {} };
      })
      .addCase(fetchCompanySettings.fulfilled, (state, action) => {
        state.companySettings = action.payload.data;
      })
      .addCase(updateCompanySettings.fulfilled,(state,action)=>{
        state.companySettings = action.payload.data;
      })
      .addCase(fetchEmailTemplateSettings.fulfilled, (state, action) => {
        state.emailTemplate = action.payload.data;
      })
      .addCase(fetchReviewReminders.pending, (state) => {
        state.reviewReminders.isLoading = true;
      })
      .addCase(fetchReviewReminders.fulfilled, (state, action) => {
        state.reviewReminders.isLoading = false;
        state.reviewReminders.rules = action.payload.data?.results || [];
        state.reviewReminders.assignerOptions = action.payload.data?.assigner_options || [];
      })
      .addCase(fetchReviewReminders.rejected, (state) => {
        state.reviewReminders.isLoading = false;
      })
      .addCase(createReviewReminder.fulfilled, (state, action) => {
        state.reviewReminders.rules = [action.payload.data, ...state.reviewReminders.rules];
      })
      .addCase(updateReviewReminder.fulfilled, (state, action) => {
        state.reviewReminders.rules = state.reviewReminders.rules.map((rule) =>
          rule.id === action.payload.data?.id ? action.payload.data : rule
        );
      })
      .addCase(deleteReviewReminder.fulfilled, (state, action) => {
        state.reviewReminders.rules = state.reviewReminders.rules.filter(
          (rule) => rule.id !== action.payload
        );
      })
  },
});

export const { setFilters, setPageSizeForAll } = HomeSlice.actions;
export default HomeSlice.reducer;
